import ScoringData from '../../enums/ScoringData.js';
import PitchData from '../../enums/PitchData.js';
import RunnerSafe from '../../enums/RunnerSafe.js';
import Out from '../../enums/Out.js';
import MlbPitchCallMapper from '../../mappers/mlbEnumsMappers/MlbPitchCallMapper.js';
import MlbPlateAppearanceResultMapper from '../../mappers/mlbEnumsMappers/MlbPlateAppearanceResultMapper.js';
import MapperBase from '../../mappers/MapperBase.js';
import PlateAppearanceResult from '../../enums/PlateAppearanceResult.js';
import MlbRunnerSafeTypeMapper from '../../mappers/mlbEnumsMappers/MlbRunnerSafeTypeMapper.js';
import MlbOutTypeMapper from '../../mappers/mlbEnumsMappers/MlbOutTypeMapper.js';
import RunnersOn from '../../enums/RunnersOn.js';

class MlbScoringDataConverter {
    constructor(pitchCallMapper, paResultMapper, runnerSafeTypeMapper, outTypeMapper) {
        if (typeof(pitchCallMapper) !== 'undefined' && !(pitchCallMapper instanceof MapperBase)) {
            throw new TypeError('pitchCallMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        this.pitchCallMapper = pitchCallMapper || new MlbPitchCallMapper();

        if (typeof(paResultMapper) !== 'undefined' && !(paResultMapper instanceof MapperBase)) {
            throw new TypeError('paResultMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        this.paResultMapper = paResultMapper || new MlbPlateAppearanceResultMapper();

        if (typeof(runnerSafeTypeMapper) !== 'undefined' && !(runnerSafeTypeMapper instanceof MapperBase)) {
            throw new TypeError('runnerSafeTypeMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        this.runnerSafeTypeMapper = runnerSafeTypeMapper || new MlbRunnerSafeTypeMapper();

        if (typeof(outTypeMapper) !== 'undefined' && !(outTypeMapper instanceof MapperBase)) {
            throw new TypeError('outTypeMapper must be undefined (to use default implementation) or MapperBase\'s class instance');
        }
        this.outTypeMapper = outTypeMapper || new MlbOutTypeMapper();

        this.scoringTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/data/result-scoring-pitch-v2.json
            [ScoringData.BattedBallLocation.key]: {
                x: null, y: null, fielderId: null
            },
            [ScoringData.PitchCall.key]: null,
            [ScoringData.PlateAppearanceResult.key]: null,
            [ScoringData.Runner1Id.key]: null,
            [ScoringData.Runner2Id.key]: null,
            [ScoringData.Runner3Id.key]: null,
            [ScoringData.CatcherId.key]: null,
            [ScoringData.Runs.key]: [],
            [ScoringData.Outs.key]: [],
            [ScoringData.RunnerSafe.key]: [],
            [ScoringData.Errors.key]: [],
            // counters
            [ScoringData.HomeTeamScore.key]: 0,
            [ScoringData.AwayTeamScore.key]: 0,
            [ScoringData.AfterPlayPitchOfPa.key]: 0,
            [ScoringData.BeforePlayPitchOfPa.key]: 0,
            // backward compatible data, only required by DTO
            [PitchData.BeforePlayInning.key]: 1,
            [PitchData.BeforePlayInningIsTop.key]: true,
            [PitchData.BeforePlayRunnersOn.key]: 0,
            [PitchData.BeforePlayBalls.key]: 0,
            [PitchData.BeforePlayStrikes.key]: 0,
            [PitchData.BeforePlayOuts.key]: 0,
            [PitchData.BeforePlayPlateAppearance.key]: 1,
            [PitchData.AfterPlayRuns.key]: 0,
            [PitchData.AfterPlayPitchCount.key]: 0,
            [PitchData.AfterPlayErrors.key]: 0,
            [PitchData.AfterPlayTotalRuns.key]: 0,
            [PitchData.BeforePlayRuns.key]: 0,
            [PitchData.BeforePlayPitchCount.key]: 0,
            [PitchData.BeforePlayErrors.key]: 0,
            [PitchData.BeforePlayTotalRuns.key]: 0,
            [PitchData.AfterPlayRunnersOn.key]: 0,
            [PitchData.AfterPlayBalls.key]: 0,
            [PitchData.AfterPlayStrikes.key]: 0,
            [PitchData.AfterPlayOuts.key]: 0,
            [PitchData.AfterPlayPlateAppearance.key]: 1,
        };

        this.runnerSafeTemplate = {
            [RunnerSafe.RunnerId.key]: null,
            [RunnerSafe.TargetBase.key]: null,
            [RunnerSafe.FromBase.key]: null,
            [RunnerSafe.Type.key]: null,
        };

        this.outTemplate = {
            [Out.Type.key]: null,
            [Out.OutTagId.key]: null,
            [Out.OutAtBase.key]: null,
            [Out.FielderId.key]: null,
            [Out.AssistorsId.key]: [],
            [Out.FielderLocation.key]: null,
            [Out.AssistorsLocations.key]: [],
        };

        // TODO - inject some converter ?
        // unfortunately hitData.coordinates looks like image coordiantes ? (pixels)
        // TODO - check if we can make it more precise, I've based on the following example:
        // totalDistance: 246yd, coordX: 140.1, coordY: 79.21 (liveData.plays.allPlays[3].playEvents[6].hitData.coordinates from https://www.mlb.com/gameday/braves-vs-pirates/2021/07/07/633330#game_state=final,game_tab=play-by-play,game=633330)
        // so totalDistance will be 225m,
        // distance in pixels is (140.1^2 + 79.21^2)^0.5 = 160.94 px
        // 1px = 1.398m (225 m/160,94 px)
        this.pxToMScale = 1.398;

        this.reset();
    }

    /**
     * Returns object with pitch and hit property from a single play event
     * NOTE - this class is unfortunately managing its own state due to docmplexiity of baseball scoring data, we could make it stateless by passing collection of play events instead of single item
     * @param {Object} playEvent - item from liveData.plays.allPlays[].playEvents, Note, only playEvents of type "ptich" are supported
     */
    convert(play, playEvent) {
        const resultScoring = this.createScoringTemplate();
        const { batter, pitcher } = play.matchup;
        let prevResult, prevResultOfOpponent = null;

        resultScoring[PitchData.BeforePlayPitchCount.key] = this.getPitchCount(pitcher.id);
        resultScoring[PitchData.BeforePlayPlateAppearance.key] = this.getPlateAppearanceNumber(batter.id);
        resultScoring[PitchData.AfterPlayPlateAppearance.key] = this.getPlateAppearanceNumber(batter.id);

        if (play.about) {
            resultScoring[PitchData.BeforePlayInning.key] = play.about.inning;
            resultScoring[PitchData.BeforePlayInningIsTop.key] = play.about.isTopInning;

            prevResult = play.about.isTopInning ? this.prevAwayResult: this.prevHomeResult;
            prevResultOfOpponent = play.about.isTopInning ? this.prevHomeResult: this.prevAwayResult;

            // home and away score goes here
            resultScoring[ScoringData.HomeTeamScore.key] = play.about.isTopInning
                ? prevResultOfOpponent[PitchData.AfterPlayTotalRuns.key]
                : prevResult[PitchData.AfterPlayTotalRuns.key];
            resultScoring[ScoringData.AwayTeamScore.key] = play.about.isTopInning
                ? prevResult[PitchData.AfterPlayTotalRuns.key]
                : prevResultOfOpponent[PitchData.AfterPlayTotalRuns.key];
        }

        if (playEvent.count) {
            resultScoring[PitchData.AfterPlayBalls.key] = playEvent.count.balls;
            resultScoring[PitchData.AfterPlayStrikes.key] = playEvent.count.strikes;
            resultScoring[PitchData.AfterPlayOuts.key] = playEvent.count.outs;
        }

        if (playEvent.details && playEvent.details.call) {
            resultScoring[ScoringData.PitchCall.key] = this.pitchCallMapper.map(playEvent.details.call.code);
            // TODO - check how MLB is handling BALK here
            // TODO - check how to handle pitching substitution
            resultScoring[PitchData.AfterPlayPitchCount.key] = this.recordPitch(pitcher.id);
        }

        const isFinalPitchInPa = playEvent.index === play.pitchIndex[play.pitchIndex.length - 1]
        const isFirstPitchInPa = playEvent.index === play.pitchIndex[0];

        // map PA result
        if (isFinalPitchInPa) {
            if (play && play.result) {
                resultScoring[ScoringData.PlateAppearanceResult.key] = this.paResultMapper.map(play.result);
                this.recordPa(batter.id);
            }
        }
        else {
            resultScoring[ScoringData.PlateAppearanceResult.key] = PlateAppearanceResult.NotAvailable.key;
        }

        // read movement of runners
        if (prevResult) {
            // read prev state if possible, TODO - consider removing it, at all movements should be reflected in this entity
            resultScoring[ScoringData.Runner1Id.key] = prevResult[ScoringData.Runner1Id.key];
            resultScoring[ScoringData.Runner2Id.key] = prevResult[ScoringData.Runner2Id.key];
            resultScoring[ScoringData.Runner3Id.key] = prevResult[ScoringData.Runner3Id.key];
        }

        if (play.runners) {
            const onThisPitch = play.runners.filter(r => r.details.playIndex === playEvent.index);
            this.readRunnersMovements(onThisPitch, play, resultScoring, prevResult);
        }

        this.updateCounters(resultScoring, play, prevResult, isFirstPitchInPa, isFinalPitchInPa);

        if (play.about) {
            play.about.isTopInning
            ? this.prevAwayResult = resultScoring
            : this.prevHomeResult = resultScoring;
        }

        return resultScoring;
    }

    reset() {
        this.plateApperancesPerBatter = {};
        this.pitchCountPerPitcher = {};
        this.prevHomeResult = this.createScoringTemplate();
        this.prevAwayResult = this.createScoringTemplate();

        this.prevHomeResult[PitchData.BeforePlayInningIsTop.key] = false;
    }

    createScoringTemplate() {
        const scoring = Object.assign({}, this.scoringTemplate);
        // we need to loose references on arrays and objects
        scoring[ScoringData.BattedBallLocation.key] = Object.assign(
            {}, this.scoringTemplate[ScoringData.BattedBallLocation.key]
        );
        scoring[ScoringData.Runs.key] = [];
        scoring[ScoringData.Outs.key] = [];
        scoring[ScoringData.RunnerSafe.key] = [];
        scoring[ScoringData.Errors.key] = [];

        return scoring;
    }

    updateCounters(resultScoring, play, prevResult, isFirstPitchInPa, isFinalPitchInPa) {
        if (prevResult) {
            resultScoring[PitchData.BeforePlayPitchCount.key] = prevResult[PitchData.AfterPlayPitchCount.key];
            // handle before pitch values
            if (prevResult[PitchData.BeforePlayInning.key] !== play.about.inning) {
                // if the inning changed, we can reset some counters
                resultScoring[PitchData.BeforePlayBalls.key] = 0;
                resultScoring[PitchData.BeforePlayStrikes.key] = 0;
                resultScoring[PitchData.BeforePlayOuts.key] = 0;
                resultScoring[PitchData.BeforePlayRunnersOn.key] = 0;
                resultScoring[PitchData.BeforePlayErrors.key] = 0;
                resultScoring[PitchData.BeforePlayRuns.key] = 0;
                resultScoring[PitchData.BeforePlayTotalRuns.key] = prevResult[PitchData.AfterPlayTotalRuns.key];
            }
            else {
                resultScoring[PitchData.BeforePlayOuts.key] = prevResult[PitchData.AfterPlayOuts.key];
                resultScoring[PitchData.BeforePlayRunnersOn.key] = prevResult[PitchData.AfterPlayRunnersOn.key];
                resultScoring[PitchData.BeforePlayErrors.key] = prevResult[PitchData.AfterPlayErrors.key];
                resultScoring[PitchData.BeforePlayRuns.key] = prevResult[PitchData.AfterPlayRuns.key];
            }

            if (!isFirstPitchInPa) {
                resultScoring[ScoringData.BeforePlayPitchOfPa.key] = prevResult[ScoringData.AfterPlayPitchOfPa.key];
                resultScoring[PitchData.BeforePlayBalls.key] = prevResult[PitchData.AfterPlayBalls.key];
                resultScoring[PitchData.BeforePlayStrikes.key] = prevResult[PitchData.AfterPlayStrikes.key];
            }
        }

        if (!isFinalPitchInPa) {
            resultScoring[ScoringData.AfterPlayPitchOfPa.key] = (
                resultScoring[ScoringData.BeforePlayPitchOfPa.key]
                + resultScoring[PitchData.AfterPlayPitchCount.key]
                - resultScoring[PitchData.BeforePlayPitchCount.key]
            );
        }

        // check if we are analyzing first pitch in this PA...
        if (isFirstPitchInPa) {
            resultScoring[ScoringData.BeforePlayPitchOfPa.key] = 0;
            resultScoring[PitchData.BeforePlayBalls.key] = 0;
            resultScoring[PitchData.BeforePlayStrikes.key] = 0;
        }

        // include what happens in this play in all counters
        resultScoring[PitchData.AfterPlayErrors.key] = (
            resultScoring[PitchData.BeforePlayErrors.key]
            + resultScoring[ScoringData.Errors.key].length
        );
        resultScoring[PitchData.AfterPlayRuns.key] = (
            resultScoring[PitchData.BeforePlayRuns.key]
            + resultScoring[ScoringData.Runs.key].length
        );
        resultScoring[PitchData.AfterPlayTotalRuns.key] = (
            resultScoring[PitchData.BeforePlayTotalRuns.key]
            + resultScoring[ScoringData.Runs.key].length
        );
        resultScoring[PitchData.AfterPlayOuts.key] = (
            resultScoring[PitchData.BeforePlayOuts.key]
            + resultScoring[ScoringData.Outs.key].length
        );
        resultScoring[PitchData.AfterPlayRunnersOn.key] = this.calculateRunnersOn(
            resultScoring[ScoringData.Runner1Id.key],
            resultScoring[ScoringData.Runner2Id.key],
            resultScoring[ScoringData.Runner3Id.key]
        );
    }

    readRunnersMovements(onThisPitch, play, resultScoring) {
        let runner1 = resultScoring[ScoringData.Runner1Id.key];
        let runner2 = resultScoring[ScoringData.Runner2Id.key];
        let runner3 = resultScoring[ScoringData.Runner3Id.key];

        // and see what happend at this pitch
        for (let r = 0; r < onThisPitch.length; r++) {
            const runnerMovement = onThisPitch[r];
            if (!runnerMovement.movement) {
                continue;
            }

            const { start, end } = runnerMovement.movement;
            const runnerId = runnerMovement.details.runner.id;
            if (runnerMovement.movement.isOut) {
                const out = this.buildOut(runnerMovement);

                if (out[Out.OutTagId.key] === runner1) {
                    runner1 = null;
                }
                else if (out[Out.OutTagId.key] === runner2) {
                    runner2 = null;
                }
                else if (out[Out.OutTagId.key] === runner3) {
                    runner3 = null;
                }
                // TODO - maybe throw otherwise

                resultScoring[ScoringData.Outs.key].push(out);
            }
            else {
                if (start === null) {
                    // batter reaching base
                    switch (end) {
                        case '1B':
                            runner1 = runnerId;
                            break;
                        case '2B':
                            runner2 = runnerId;
                            break;
                        case '3B':
                            runner3 = runnerId;
                            break;
                        case 'score':
                        case '4B':
                            resultScoring[ScoringData.Runs.key].push(runnerId);
                            break;
                    }
                }
                else {
                    const runnerSafe = this.buildRunnersSafe(runnerMovement);
                    switch (runnerSafe[RunnerSafe.FromBase.key]) {
                        case 1:
                            runner1 = null;
                            break;
                        case 2:
                            runner2 = null;
                            break;
                        case 3:
                            runner3 = null;
                            break;
                    }

                    switch (runnerSafe[RunnerSafe.TargetBase.key]) {
                        case 1:
                            runner1 = runnerSafe[RunnerSafe.RunnerId.key];
                            break;
                        case 2:
                            runner2 = runnerSafe[RunnerSafe.RunnerId.key];
                            break;
                        case 3:
                            runner3 = runnerSafe[RunnerSafe.RunnerId.key];
                            break;
                        case 4:
                            resultScoring[ScoringData.Runs.key].push(runnerId);
                    }

                    resultScoring[ScoringData.RunnerSafe.key].push(runnerSafe);
                }
            }

            // read errors and fielded balls... stored as credits
            if (runnerMovement.credits) {
                this.readFieldingCredits(runnerMovement, resultScoring, play);
            }

            resultScoring[ScoringData.Runner1Id.key] = runner1;
            resultScoring[ScoringData.Runner2Id.key] = runner2;
            resultScoring[ScoringData.Runner3Id.key] = runner3;
        }
    }

    buildOut(runnerMovement) {
        const out = Object.assign({}, this.outTemplate);
        // we need to loose referecnes
        out[Out.AssistorsId.key] = [];
        out[Out.AssistorsLocations.key] = [];

        out[Out.OutTagId.key] = runnerMovement.details.runner.id;
        out[Out.OutAtBase.key] = this.convertBaseNumber(runnerMovement.movement.outBase);
        out[Out.Type.key] = this.outTypeMapper.map(runnerMovement.details);

        if (runnerMovement.credits) {
            for (let c = 0; c < runnerMovement.credits.length; c++) {
                const credit = runnerMovement.credits[c];
                if (credit.credit === 'f_putout') {
                    out[Out.FielderId.key] = credit.player.id;
                }
                else if (credit.credit === 'f_assist') {
                    out[Out.AssistorsId.key].push(credit.player.id);
                }
                // TODO - can we get positions of fielders somehow ?
            }
        }

        return out;
    }

    buildRunnersSafe(runnerMovement) {
        const runnerSafe = Object.assign({}, this.runnerSafeTemplate);
        const from = this.convertBaseNumber(runnerMovement.movement.start);
        const to = this.convertBaseNumber(runnerMovement.movement.end);

        runnerSafe[RunnerSafe.FromBase.key] = from;
        runnerSafe[RunnerSafe.TargetBase.key] = to;
        runnerSafe[RunnerSafe.RunnerId.key] = runnerMovement.details.runner.id;
        runnerSafe[RunnerSafe.Type.key] = this.runnerSafeTypeMapper.map(runnerMovement.details);

        return runnerSafe;
    }

    readFieldingCredits(runnerMovement, resultScoring, play) {
        for (let c = 0; c < runnerMovement.credits.length; c++) {
            const credit = runnerMovement.credits[c];
            if (credit.credit === 'f_fielded_ball') {
                resultScoring[ScoringData.BattedBallLocation.key].fielderId = credit.player.id;
                // read coords from hit
                const hitData = play.playEvents[runnerMovement.details.playIndex].hitData;
                if (hitData && hitData.coordinates) {
                    // try to convert pixel coords to meters
                    // TODO - introduce some common converter
                    resultScoring[ScoringData.BattedBallLocation.key].x = hitData.coordinates.coordX * this.pxToMScale;
                    resultScoring[ScoringData.BattedBallLocation.key].y = hitData.coordinates.coordY * this.pxToMScale;
                }

            }
            else if (
                credit.credit === 'f_fielding_error'
                || credit.credit === 'f_throwing_error'
            ) {
                resultScoring[ScoringData.Errors.key].push(credit.player.id);
            }
            // else if (credit.credit === 'f_deflection') {
                // TODO - IDK what to do with this, it looks like sometimes fielder can luckily change the trajectory of the ball which makes it easier to field etc
                // @see https://www.youtube.com/watch?v=0zXXPX-sWNU
                // from what I know it doens't have any meaning
            // }
        }
    }

    calculateRunnersOn(runner1Id, runner2Id, runner3Id) {
        // lets analyze it in the natural order of bases
        if (runner1Id) {
            if (runner2Id) {
                if (runner3Id) {
                    return RunnersOn.All.key;
                }
                return RunnersOn.FirstAndSecond.key;
            }
            if (runner3Id) {
                return RunnersOn.FirstAndThird.key;
            }

            return RunnersOn.First.key;
        }

        // so not on man on first
        if (runner2Id) {
            if (runner3Id) {
                return RunnersOn.SecondAndThird.key;
            }
            return RunnersOn.Second.key;
        }
        // no man on second
        if (runner3Id) {
            return RunnersOn.Third.key;
        }

        // no runners
        return RunnersOn.NoRunners.key;
    }

    recordPitch(pitcherId) {
        if (!this.pitchCountPerPitcher.hasOwnProperty(pitcherId)) {
            this.pitchCountPerPitcher[pitcherId] = 0;
        }
        this.pitchCountPerPitcher[pitcherId]++;

        return this.pitchCountPerPitcher[pitcherId];
    }

    getPitchCount(pitcherId) {
        if (!this.pitchCountPerPitcher.hasOwnProperty(pitcherId)) {
            return 0;
        }

        return this.pitchCountPerPitcher[pitcherId];
    }

    recordPa(batterId) {
        if (!this.plateApperancesPerBatter.hasOwnProperty(batterId)) {
            this.plateApperancesPerBatter[batterId] = 1;
        }
        this.plateApperancesPerBatter[batterId]++;

        return this.plateApperancesPerBatter[batterId];
    }

    getPlateAppearanceNumber(batterId) {
        if (!this.plateApperancesPerBatter.hasOwnProperty(batterId)) {
            return 1;
        }

        return this.plateApperancesPerBatter[batterId];
    }

    convertBaseNumber(baseName) {
        switch (baseName) {
            case '1B':
                return 1;
            case '2B':
                return 2;
            case '3B':
                return 3;
            case 'score':
            case '4B':
                return 4;
        }

        throw new Error(`Unknown base - ${baseName}`);
    }
}

export default MlbScoringDataConverter;